import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_MEDIA, formatCode, MEDIA_PROFILES, MEDIA_PROFILE_LABELS } from '@shared/protocol';
import type { MediaProfile, MeetingHostView } from '@shared/protocol';
import { Masthead } from '../components/Masthead';
import { CopyIcon } from '../components/Icons';
import { api, ApiError } from '../lib/api';

export default function HostPage() {
  const [host, setHost] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    api
      .session()
      .then((s) => setHost(s.host))
      .catch(() => setHost(null));
  }, []);

  if (host === undefined) {
    return (
      <div className="shell">
        <Masthead />
        <main className="page">
          <p className="faint">Loading…</p>
        </main>
      </div>
    );
  }

  return host ? <Dashboard host={host} onSignOut={() => setHost(null)} /> : <SignIn onSignedIn={setHost} />;
}

/* ------------------------------------------------------------------ */

function SignIn({ onSignedIn }: { onSignedIn: (host: string) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { host } = await api.logIn(username, password);
      onSignedIn(host);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell">
      <Masthead />
      <main className="page">
        <h1>Sign in to start a meeting</h1>
        <p className="lede">
          Only the host signs in. Everyone else joins with the link — no account, no sign-in.
        </p>

        <form className="grid" onSubmit={submit} style={{ marginTop: '2rem', maxWidth: '26rem' }}>
          <div className="cell cell-raised stack">
            <div>
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error ? (
              <p className="notice bad" role="alert">
                {error}
              </p>
            ) : null}
            <button className="primary" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** 0 means the link never expires. */
const LIFETIMES: { label: string; hours: number }[] = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
  { label: 'Never — permanent link', hours: 0 },
];

const DEFAULT_LIFETIME = 24 * 30;

function Dashboard({ host, onSignOut }: { host: string; onSignOut: () => void }) {
  const [meetings, setMeetings] = useState<MeetingHostView[]>([]);
  const [title, setTitle] = useState('');
  const [lifetime, setLifetime] = useState(DEFAULT_LIFETIME);
  const [media, setMedia] = useState<MediaProfile>(DEFAULT_MEDIA);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { meetings } = await api.listMeetings();
      setMeetings(meetings);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load meetings');
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Participant counts are live data; a slow poll is enough and avoids a
    // second WebSocket just for the dashboard.
    const timer = setInterval(() => void refresh(), 15_000);
    return () => clearInterval(timer);
  }, [refresh]);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const created = await api.createMeeting(title.trim() || 'Meeting', lifetime, media);
      setMeetings((prev) => [created, ...prev]);
      setTitle('');
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the meeting');
    } finally {
      setBusy(false);
    }
  }

  /** Optimistic, then reconciled — the select should not lag a round trip. */
  async function retune(code: string, profile: MediaProfile) {
    setMeetings((prev) => prev.map((m) => (m.code === code ? { ...m, media: profile } : m)));
    try {
      const updated = await api.setMeetingMedia(code, profile);
      setMeetings((prev) => prev.map((m) => (m.code === code ? updated : m)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the quality');
      void refresh();
    }
  }

  async function remove(code: string) {
    setMeetings((prev) => prev.filter((m) => m.code !== code));
    try {
      await api.deleteMeeting(code);
    } catch {
      void refresh();
    }
  }

  return (
    <div className="shell">
      <Masthead>
        <span>{host}</span>
        <button
          className="link"
          onClick={async () => {
            await api.logOut().catch(() => {});
            onSignOut();
          }}
        >
          Sign out
        </button>
      </Masthead>

      <main className="page stack" style={{ gap: '2.5rem' }}>
        <section>
          <h1>Meetings</h1>
          <p className="lede">
            Create a meeting, then send the link. Anyone with the link can join; the link is the only
            credential, so treat it like one.
          </p>

          <form className="row" onSubmit={create} style={{ marginTop: '1.5rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: '15rem' }}>
              <label htmlFor="title">Meeting name</label>
              <input
                id="title"
                type="text"
                placeholder="Weekly catch-up"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
              />
            </div>
            <div style={{ minWidth: '12rem' }}>
              <label htmlFor="lifetime">Link lasts</label>
              <select
                id="lifetime"
                value={lifetime}
                onChange={(e) => setLifetime(Number(e.target.value))}
              >
                {LIFETIMES.map((l) => (
                  <option key={l.hours} value={l.hours}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: '14rem' }}>
              <label htmlFor="media">Quality</label>
              <select
                id="media"
                value={media}
                onChange={(e) => setMedia(e.target.value as MediaProfile)}
              >
                {MEDIA_PROFILES.map((p) => (
                  <option key={p} value={p}>
                    {MEDIA_PROFILE_LABELS[p].name}
                  </option>
                ))}
              </select>
            </div>
            <button className="primary" type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create meeting'}
            </button>
          </form>

          {/* The chosen preset explains itself rather than making the host open
              docs to find out what "studio" costs them. */}
          <p className="faint" style={{ marginTop: '0.75rem' }}>
            {MEDIA_PROFILE_LABELS[media].detail}
          </p>

          {lifetime === 0 ? (
            <p className="faint" style={{ marginTop: '0.5rem' }}>
              A permanent link works until you end it. Good for a standing weekly call — but
              since the link is the only credential, it is worth ending and reissuing if it
              ever spreads further than you meant.
            </p>
          ) : null}
        </section>

        {error ? (
          <p className="notice bad" role="alert">
            {error}
          </p>
        ) : null}

        <section>
          <h2>active links</h2>
          {meetings.length === 0 ? (
            <p className="faint" style={{ marginTop: '1rem' }}>
              No meetings yet.
            </p>
          ) : (
            <div className="meetings" style={{ marginTop: '1rem' }}>
              {meetings.map((m) => (
                <MeetingRow
                  key={m.code}
                  meeting={m}
                  onDelete={() => void remove(m.code)}
                  onSetMedia={(profile) => void retune(m.code, profile)}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function MeetingRow({
  meeting,
  onDelete,
  onSetMedia,
}: {
  meeting: MeetingHostView;
  onDelete: () => void;
  onSetMedia: (profile: MediaProfile) => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(meeting.joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked outside a secure context or without permission —
      // the link is on screen, so selecting it by hand still works.
      setCopied(false);
    }
  }

  return (
    <div className="meeting">
      <div className="meeting-main">
        <div className="meeting-title">{meeting.title}</div>
        <div className="joinlink">{meeting.joinUrl}</div>
        <div className="faint row" style={{ gap: '0.75rem' }}>
          {meeting.participantCount > 0 ? (
            <span className="row" style={{ gap: '0.375rem' }}>
              <span className="dot on" />
              {meeting.participantCount} in the call
            </span>
          ) : null}
          {/* Shown alongside occupancy, not instead of it — how long a link
              stays valid matters most for the ones in active use. */}
          <span>
            {meeting.expiresAt
              ? `Expires ${new Date(meeting.expiresAt).toLocaleString()}`
              : 'Permanent link'}
          </span>
          {/* Changeable in place: a standing link outlives the reason it was
              made, and the week it is used to play something back should not
              need a new one. Only affects people who join after the change. */}
          <label className="visually-hidden" htmlFor={`media-${meeting.code}`}>
            Quality for {meeting.title}
          </label>
          <select
            id={`media-${meeting.code}`}
            className="inline"
            value={meeting.media}
            onChange={(e) => onSetMedia(e.target.value as MediaProfile)}
            title={MEDIA_PROFILE_LABELS[meeting.media].detail}
          >
            {MEDIA_PROFILES.map((p) => (
              <option key={p} value={p}>
                {MEDIA_PROFILE_LABELS[p].name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="row">
        <button className="ghost control" onClick={() => void copy()} title="Copy join link">
          <CopyIcon />
          {copied ? 'Copied' : 'Copy'}
        </button>
        <a href={meeting.joinUrl}>
          <button className="primary">Join</button>
        </a>
        <button className="ghost" onClick={onDelete} title={`End ${meeting.title}`}>
          End
        </button>
      </div>
      <div className="visually-hidden">Code {formatCode(meeting.code)}</div>
    </div>
  );
}
